import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BookUser, Plus, Search, Trash2, Edit3, Upload, Download, Users, Phone, Mail,
  ChevronRight, ChevronLeft, MoreVertical, X, Check, AlertCircle, CheckCircle,
  FolderPlus, UserPlus, FileSpreadsheet, RefreshCw, Building2, MapPin, Briefcase, Hash
} from 'lucide-react';
import api from '../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const COLORS = ['#6366f1', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#be185d', '#4f46e5'];

const ContactBook = () => {
  // State
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  const LIMIT = 50;

  // Toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const res = await api.get('/user/contact-book/groups');
      setGroups(res.data?.groups || []);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // Fetch contacts when group or page or search changes
  const fetchContacts = useCallback(async () => {
    if (!selectedGroup) return;
    setContactsLoading(true);
    try {
      const res = await api.get(`/user/contact-book/groups/${selectedGroup.id}/contacts`, {
        params: { page, limit: LIMIT, search }
      });
      setContacts(res.data?.contacts || []);
      setContactsTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  }, [selectedGroup, page, search]);

  useEffect(() => {
    if (selectedGroup) {
      fetchContacts();
      setSelectedContacts(new Set());
    }
  }, [selectedGroup, page, search, fetchContacts]);

  // Group CRUD
  const handleSaveGroup = async (data) => {
    try {
      if (editingGroup) {
        await api.put(`/user/contact-book/groups/${editingGroup.id}`, data);
        showToast('Group updated');
      } else {
        await api.post('/user/contact-book/groups', data);
        showToast('Group created');
      }
      setShowGroupModal(false);
      setEditingGroup(null);
      fetchGroups();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save group';
      showToast(msg, 'error');
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete "${group.name}" and all its ${group.contact_count} contacts? This cannot be undone.`)) return;
    try {
      await api.delete(`/user/contact-book/groups/${group.id}`);
      showToast('Group deleted');
      if (selectedGroup?.id === group.id) {
        setSelectedGroup(null);
        setContacts([]);
      }
      fetchGroups();
    } catch (err) {
      showToast('Failed to delete group', 'error');
    }
  };

  // Contact CRUD
  const handleSaveContact = async (data) => {
    try {
      if (editingContact) {
        await api.put(`/user/contact-book/contacts/${editingContact.id}`, data);
        showToast('Contact updated');
      } else {
        await api.post(`/user/contact-book/groups/${selectedGroup.id}/contacts`, data);
        showToast('Contact added');
      }
      setShowContactModal(false);
      setEditingContact(null);
      fetchContacts();
      fetchGroups();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save contact';
      showToast(msg, 'error');
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Delete contact "${contact.name || contact.mobile}"?`)) return;
    try {
      await api.delete(`/user/contact-book/contacts/${contact.id}`);
      showToast('Contact deleted');
      fetchContacts();
      fetchGroups();
    } catch (err) {
      showToast('Failed to delete contact', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.size === 0) return;
    if (!window.confirm(`Delete ${selectedContacts.size} selected contacts?`)) return;
    try {
      await api.post('/user/contact-book/contacts/delete-bulk', { ids: Array.from(selectedContacts) });
      showToast(`${selectedContacts.size} contacts deleted`);
      setSelectedContacts(new Set());
      fetchContacts();
      fetchGroups();
    } catch (err) {
      showToast('Failed to delete contacts', 'error');
    }
  };

  // Upload
  const handleUpload = async (file) => {
    if (!file || !selectedGroup) return;
    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/user/contact-book/groups/${selectedGroup.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(res.data);
      showToast(`${res.data?.inserted || 0} contacts imported`);
      fetchContacts();
      fetchGroups();
    } catch (err) {
      const msg = err.response?.data?.error || 'Upload failed';
      showToast(msg, 'error');
    } finally {
      setUploading(false);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const headers = 'name,country_code,mobile,email,occupation,gender,age,organization,designation,address,city,state,country,zip_code';
    const sample = 'John Doe,+91,9999999999,john@example.com,Engineer,male,30,Acme Corp,Manager,123 Main St,Mumbai,Maharashtra,India,400001';
    const blob = new Blob([headers + '\n' + sample + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contact_book_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(contactsTotal / LIMIT);

  const toggleSelectAll = () => {
    if (selectedContacts.size === contacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(contacts.map(c => c.id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedContacts);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedContacts(next);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium animate-pulse" style={{
          background: toast.type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
          color: 'white',
        }}>
          {toast.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <BookUser className="w-5 h-5 text-white" />
            </div>
            Contact Book
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage contacts and groups for all services</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setEditingGroup(null); setShowGroupModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-medium text-sm transition-all hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
            <FolderPlus className="w-4 h-4" /> New Group
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left: Groups Panel */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-500" /> Groups
              </h3>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#4f46e5' }}>{groups.length}</span>
            </div>
            <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
              {groups.length === 0 ? (
                <div className="p-6 text-center">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">No groups yet</p>
                  <button onClick={() => { setEditingGroup(null); setShowGroupModal(true); }}
                    className="mt-2 text-xs font-medium text-indigo-600 hover:underline">Create your first group</button>
                </div>
              ) : (
                groups.map((group) => (
                  <div
                    key={group.id}
                    onClick={() => { setSelectedGroup(group); setPage(1); setSearch(''); }}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-all hover:bg-gray-50"
                    style={{
                      background: selectedGroup?.id === group.id ? 'rgba(99, 102, 241, 0.06)' : 'transparent',
                      borderLeft: selectedGroup?.id === group.id ? '3px solid #4f46e5' : '3px solid transparent',
                      borderBottom: '1px solid rgba(0,0,0,0.03)',
                    }}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ background: group.color || '#6366f1' }}>
                      {group.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{group.name}</p>
                      <p className="text-[10px] text-gray-400">{group.contact_count.toLocaleString()} contacts</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); setEditingGroup(group); setShowGroupModal(true); }}
                        className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600" title="Edit">
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group); }}
                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500" title="Delete">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: Contacts Panel */}
        <div className="lg:col-span-9">
          {!selectedGroup ? (
            <div className="bg-white rounded-xl p-12 text-center" style={cardStyle}>
              <BookUser className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-600 mb-2">Select a Group</h3>
              <p className="text-sm text-gray-400 mb-4">Choose a group from the left panel to view and manage contacts</p>
              <button onClick={() => { setEditingGroup(null); setShowGroupModal(true); }}
                className="px-4 py-2 rounded-xl text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-all"
                style={{ border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                <FolderPlus className="w-4 h-4 inline mr-1" /> Create New Group
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
              {/* Contacts Header */}
              <div className="px-5 py-3.5 flex items-center justify-between flex-wrap gap-3" style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.06), rgba(6, 182, 212, 0.04))', borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: selectedGroup.color || '#6366f1' }}>
                    {selectedGroup.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">{selectedGroup.name}</h3>
                    <p className="text-[10px] text-gray-400">{contactsTotal.toLocaleString()} contacts</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setEditingContact(null); setShowContactModal(true); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white transition-all hover:shadow-md"
                    style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
                    <UserPlus className="w-3.5 h-3.5" /> Add Contact
                  </button>
                  <button onClick={() => setShowUploadModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-sm"
                    style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                    <Upload className="w-3.5 h-3.5" /> Bulk Upload
                  </button>
                  <button onClick={downloadTemplate}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:shadow-sm"
                    style={{ background: 'rgba(99, 102, 241, 0.06)', color: '#6366f1', border: '1px solid rgba(99, 102, 241, 0.12)' }}>
                    <Download className="w-3.5 h-3.5" /> Template
                  </button>
                  {selectedContacts.size > 0 && (
                    <button onClick={handleBulkDelete}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 transition-all"
                      style={{ border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete ({selectedContacts.size})
                    </button>
                  )}
                </div>
              </div>

              {/* Search */}
              <div className="px-5 py-3" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    placeholder="Search by name, mobile, or email..."
                    className="w-full pl-10 pr-4 py-2 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}
                  />
                </div>
              </div>

              {/* Contacts Table */}
              {contactsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
                </div>
              ) : contacts.length === 0 ? (
                <div className="p-10 text-center">
                  <UserPlus className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-400 mb-2">{search ? 'No contacts match your search' : 'No contacts in this group yet'}</p>
                  {!search && (
                    <div className="flex items-center justify-center gap-3">
                      <button onClick={() => { setEditingContact(null); setShowContactModal(true); }}
                        className="text-xs font-medium text-indigo-600 hover:underline">Add a contact</button>
                      <span className="text-gray-300">or</span>
                      <button onClick={() => setShowUploadModal(true)}
                        className="text-xs font-medium text-emerald-600 hover:underline">Upload CSV</button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                          <th className="px-4 py-2.5 text-left w-10">
                            <input type="checkbox" checked={selectedContacts.size === contacts.length && contacts.length > 0}
                              onChange={toggleSelectAll} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                          </th>
                          <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Mobile</th>
                          <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                          <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">City</th>
                          <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Organization</th>
                          <th className="px-4 py-2.5 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider w-20">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((ct) => (
                          <tr key={ct.id} className="hover:bg-gray-50/50 transition-colors" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.04)' }}>
                            <td className="px-4 py-2.5">
                              <input type="checkbox" checked={selectedContacts.has(ct.id)}
                                onChange={() => toggleSelect(ct.id)} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                                  style={{ background: selectedGroup.color || '#6366f1' }}>
                                  {(ct.name || ct.mobile).charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm font-medium text-gray-800 truncate max-w-[150px]">{ct.name || '—'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="text-xs font-mono text-gray-700">{ct.country_code}{ct.mobile}</span>
                            </td>
                            <td className="px-4 py-2.5 hidden md:table-cell">
                              <span className="text-xs text-gray-500 truncate max-w-[180px] block">{ct.email || '—'}</span>
                            </td>
                            <td className="px-4 py-2.5 hidden lg:table-cell">
                              <span className="text-xs text-gray-500">{ct.city || '—'}</span>
                            </td>
                            <td className="px-4 py-2.5 hidden lg:table-cell">
                              <span className="text-xs text-gray-500 truncate max-w-[120px] block">{ct.organization || '—'}</span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => { setEditingContact(ct); setShowContactModal(true); }}
                                  className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-all" title="Edit">
                                  <Edit3 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleDeleteContact(ct)}
                                  className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all" title="Delete">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="px-5 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.05)' }}>
                      <p className="text-xs text-gray-400">
                        Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, contactsTotal)} of {contactsTotal.toLocaleString()}
                      </p>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-500">
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="px-3 py-1 text-xs font-semibold text-gray-600">{page} / {totalPages}</span>
                        <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed text-gray-500">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Group Modal */}
      {showGroupModal && (
        <GroupModal
          group={editingGroup}
          onSave={handleSaveGroup}
          onClose={() => { setShowGroupModal(false); setEditingGroup(null); }}
        />
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <ContactModal
          contact={editingContact}
          onSave={handleSaveContact}
          onClose={() => { setShowContactModal(false); setEditingContact(null); }}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          groupName={selectedGroup?.name}
          uploading={uploading}
          uploadResult={uploadResult}
          onUpload={handleUpload}
          onDownloadTemplate={downloadTemplate}
          onClose={() => { setShowUploadModal(false); setUploadResult(null); }}
        />
      )}
    </div>
  );
};

// ============================================================
// GROUP MODAL
// ============================================================
const GroupModal = ({ group, onSave, onClose }) => {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [color, setColor] = useState(group?.color || '#6366f1');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), description, color });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <h3 className="text-base font-bold text-gray-900">{group ? 'Edit Group' : 'Create New Group'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Group Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus
              className="w-full px-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }} placeholder="e.g. VIP Customers" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Description</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }} placeholder="Optional description" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Color</label>
            <div className="flex items-center gap-2">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  className="w-7 h-7 rounded-full transition-all" style={{
                    background: c,
                    border: color === c ? '3px solid #1e1b4b' : '2px solid transparent',
                    transform: color === c ? 'scale(1.15)' : 'scale(1)',
                  }} />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-all">Cancel</button>
            <button type="submit" className="px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              {group ? 'Update' : 'Create'} Group
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// CONTACT MODAL
// ============================================================
const ContactModal = ({ contact, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: contact?.name || '',
    country_code: contact?.country_code || '+91',
    mobile: contact?.mobile || '',
    email: contact?.email || '',
    occupation: contact?.occupation || '',
    gender: contact?.gender || '',
    age: contact?.age || '',
    organization: contact?.organization || '',
    designation: contact?.designation || '',
    address: contact?.address || '',
    city: contact?.city || '',
    state: contact?.state || '',
    country: contact?.country || 'India',
    zip_code: contact?.zip_code || '',
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.mobile.trim()) return;
    const data = { ...form, age: form.age ? parseInt(form.age) : null };
    onSave(data);
  };

  const renderField = (label, field, { type = 'text', required = false, placeholder = '', half = false } = {}) => (
    <div className={half ? 'flex-1 min-w-0' : ''} key={field}>
      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input type={type} value={form[field]} onChange={(e) => update(field, e.target.value)} required={required}
        className="w-full px-3 py-2 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
        style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }} placeholder={placeholder || label} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 flex items-center justify-between sticky top-0 bg-white z-10" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <h3 className="text-base font-bold text-gray-900">{contact ? 'Edit Contact' : 'Add New Contact'}</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Primary Info */}
          <div className="flex items-center gap-2 mb-1">
            <Phone className="w-4 h-4 text-indigo-500" />
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Primary Info</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {renderField('Name', 'name', { placeholder: 'Full Name' })}
            <div className="flex gap-2">
              <div className="w-20">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">Code</label>
                <input type="text" value={form.country_code} onChange={(e) => update('country_code', e.target.value)}
                  className="w-full px-2 py-2 text-sm rounded-lg text-center focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }} />
              </div>
              {renderField('Mobile Number', 'mobile', { required: true, placeholder: '9999999999', half: true })}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {renderField('Email', 'email', { type: 'email', placeholder: 'email@example.com' })}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 block">Gender</label>
                <select value={form.gender} onChange={(e) => update('gender', e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              {renderField('Age', 'age', { type: 'number', placeholder: '25', half: true })}
            </div>
          </div>

          {/* Professional */}
          <div className="flex items-center gap-2 mt-4 mb-1">
            <Briefcase className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Professional</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {renderField('Occupation', 'occupation', { placeholder: 'Engineer' })}
            {renderField('Organization', 'organization', { placeholder: 'Company Name' })}
            {renderField('Designation', 'designation', { placeholder: 'Manager' })}
          </div>

          {/* Address */}
          <div className="flex items-center gap-2 mt-4 mb-1">
            <MapPin className="w-4 h-4 text-emerald-500" />
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Address</span>
          </div>
          {renderField('Address', 'address', { placeholder: 'Street address' })}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {renderField('City', 'city', { placeholder: 'Mumbai' })}
            {renderField('State', 'state', { placeholder: 'Maharashtra' })}
            {renderField('Country', 'country', { placeholder: 'India' })}
            {renderField('Zip Code', 'zip_code', { placeholder: '400001' })}
          </div>

          <div className="flex items-center justify-end gap-3 pt-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.08)' }}>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-all">Cancel</button>
            <button type="submit" className="px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              {contact ? 'Update' : 'Add'} Contact
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// UPLOAD MODAL
// ============================================================
const UploadModal = ({ groupName, uploading, uploadResult, onUpload, onDownloadTemplate, onClose }) => {
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }
    onUpload(file);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <h3 className="text-base font-bold text-gray-900">Bulk Upload Contacts</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500">Upload a CSV file to import contacts into <strong className="text-gray-800">{groupName}</strong>.</p>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
            className="rounded-xl p-8 text-center cursor-pointer transition-all"
            style={{
              border: dragOver ? '2px dashed #4f46e5' : '2px dashed rgba(99, 102, 241, 0.2)',
              background: dragOver ? 'rgba(99, 102, 241, 0.04)' : 'rgba(99, 102, 241, 0.02)',
            }}
          >
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
                <p className="text-sm font-medium text-indigo-600">Uploading and processing...</p>
              </div>
            ) : (
              <>
                <FileSpreadsheet className="w-10 h-10 text-indigo-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-600 mb-1">Drag & drop your CSV file here</p>
                <p className="text-xs text-gray-400">or click to browse</p>
              </>
            )}
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div className="rounded-xl p-4" style={{ background: 'rgba(16, 185, 129, 0.05)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
              <h4 className="text-xs font-bold text-emerald-700 mb-2">Upload Complete</h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-lg font-bold text-emerald-600">{uploadResult.inserted}</p>
                  <p className="text-[10px] text-gray-500">Inserted</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-amber-600">{uploadResult.duplicates}</p>
                  <p className="text-[10px] text-gray-500">Duplicates</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-red-600">{uploadResult.errors}</p>
                  <p className="text-[10px] text-gray-500">Errors</p>
                </div>
              </div>
            </div>
          )}

          {/* Template download */}
          <div className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <FileSpreadsheet className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-600">CSV must have a <code className="px-1 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-mono">mobile</code> column. Other columns: name, email, country_code, occupation, gender, age, organization, designation, address, city, state, country, zip_code.</p>
              <button onClick={onDownloadTemplate} className="mt-1 text-xs font-semibold text-indigo-600 hover:underline">Download CSV Template</button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-all">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactBook;
